import React, { useState } from 'react';
import { GameState, GlobalModifierInfo, Investigator, MythosCard, UserAction } from '../api';
import { GameDialog } from './GameDialog';
import { SimpleMarkdown } from './SimpleMarkdown';
import { rumorProgress } from '../utils/gamePresentation';
import { AncientOneDisplay } from './AncientOneDisplay';
import { HeraldCard } from './HeraldCard';
import { GuardianCard } from './GuardianCard';
import { InstitutionDisplay } from './InstitutionDisplay';
import { getOngoingDifficultyModifier } from '../utils/difficultyPresentation';
import { InvestigatorInspector } from './InvestigatorInspector';
import { Card, StackedItemWithKind } from './Card';
import { orderedGameInvestigators } from './investigatorOrder';

type RuleGroup = { key: string; name: string; type: string; card?: MythosCard; modifiers: GlobalModifierInfo[]; summary?: string; artwork?: string; rules?: string[]; item?: StackedItemWithKind; owner?: Investigator };
const ruleTypeLabel = (group: RuleGroup) => {
  if (group.type === 'AncientOne') return 'Ancient One';
  if (group.type === 'UniqueItem') return 'Unique Item';
  if (group.type === 'CommonItem') return 'Common Item';
  const processType = group.card?.processType?.trim();
  return group.type === 'Process' && processType ? `Process (${processType})` : group.type;
};
export const buildRuleGroups = (state: GameState): RuleGroup[] => {
  const groups: RuleGroup[] = [];
  const add = (name: string, type: string, card?: MythosCard, artwork?: string, summary?: string, identity?: string) => {
    const key = `rule-${type}-${identity ?? name}`;
    const existing = groups.find(group => group.key === key);
    if (existing) return existing;
    const group: RuleGroup = { key, name, type, card, modifiers: [], summary, artwork };
    groups.push(group); return group;
  };
  if (state.activeProcess) add(state.activeProcess.name, 'Process', state.activeProcess);
  if (state.activeRumor) add(state.activeRumor.name, 'Rumor', state.activeRumor);
  if (state.activeMythos?.shouldBeActiveForOneTurn) add(state.activeMythos.name, 'This turn', state.activeMythos);
  (state.specialCardsInPlay ?? []).forEach(card => add(card.title || card.name, 'Special card', undefined, undefined, card.description));
  (state.activeHeralds ?? []).forEach(card => add(card.name, 'Herald', undefined, `/Heralds/${encodeURIComponent(card.name)}.jpg`));
  (state.activeGuardians ?? []).forEach(card => {
    const group = add(card.name, 'Guardian', undefined, `/Guardians/${encodeURIComponent(card.name)}.jpg`,
      card.name === 'Hypnos' && state.hypnosVisionLocation ? `Vision: ${state.hypnosVisionLocation}. Draw an extra encounter there and choose one.` : undefined);
    if (card.name === 'Nodens') group.rules = [
      'Whenever an investigator gains a Blessing card, he also draws the top card of the Blessings of Nodens deck and keeps it.',
      "Instead of making a Combat check against a nightgaunt, any investigator may instead have the nightgaunt drop him into an open gate of his choice by invoking Nodens' name",
    ];
  });
  if (state.activeInstitution) {
    const institution = state.activeInstitution;
    const group = add(institution.name, 'Institution');
    group.rules = [institution.setupDescription, ...institution.rules.map(rule => rule.description)].filter(Boolean);
  }
  (state.globalModifiers ?? []).forEach(original => {
    const modifier = getOngoingDifficultyModifier(original);
    if (!modifier) return;
    const source = typeof modifier.sourceCard?.name === 'string' ? modifier.sourceCard.name : modifier.source || 'Other effects';
    const itemKind = modifier.sourceType === 'UniqueItem' ? 'unique' : modifier.sourceType === 'CommonItem' ? 'common' : modifier.sourceType === 'Spell' ? 'spell' : undefined;
    if (itemKind && modifier.sourceCard?.name) {
      const sourceItem = modifier.sourceCard;
      const collection = itemKind === 'unique' ? 'uniqueItems' : itemKind === 'common' ? 'commonItems' : 'spells';
      const owner = orderedGameInvestigators(state).find(investigator => investigator[collection]?.some(item => sourceItem.id ? item.id === sourceItem.id : item === sourceItem));
      const item = [...(owner?.[collection] ?? [])].find(item => sourceItem.id ? item.id === sourceItem.id : item === sourceItem) ?? sourceItem;
      const group = add(source, modifier.sourceType!, undefined, undefined, undefined, sourceItem.id ?? source);
      group.item = { kind: itemKind, item };
      group.owner = owner;
      if (!group.modifiers.some(value => value.id === modifier.id && value.additionalData === modifier.additionalData && value.value === modifier.value && value.used === modifier.used)) group.modifiers.push(modifier);
      return;
    }
    let group = groups.find(value => (value.name === source || value.name === modifier.sourceCard?.title) && (modifier.sourceType === 'Mythos' ? !!value.card : value.type === modifier.sourceType || value.type === 'Special card'));
    if (!group) group = add(source, modifier.sourceType || 'Global effect');
    if (!group.modifiers.some(value => value.id === modifier.id && value.additionalData === modifier.additionalData && value.value === modifier.value && value.used === modifier.used)) group.modifiers.push(modifier);
  });
  return groups;
};

export const RuleText: React.FC<{ group: RuleGroup; detailed?: boolean }> = ({ group, detailed }) => {
  const card = group.card;
  const cardDescription = card ? (card.type === 'Rumor' ? card.rumorDescription || card.textDescription : card.textDescription) || '' : '';
  const normalizeText = (text: string) => text.trim().replace(/\s+/g, ' ');
  const cardLines = new Set(cardDescription.split(/\r?\n/).map(normalizeText));
  const modifierDescriptions = group.modifiers.map(modifier => {
    const text = modifier.textDescription?.trim() || modifier.additionalData?.trim();
    const displayText = text && modifier.sourceType !== 'Difficulty' && modifier.sourceType !== 'Investigator' && modifier.sourceType !== 'Institution' && modifier.value && Math.abs(modifier.value) !== 99
      ? `${text} (${modifier.value > 0 ? '+' : ''}${modifier.value})`
      : text;
    return { text, displayText };
  });
  const descriptions = Array.from(new Set([...modifierDescriptions.map(value => value.displayText).filter(Boolean), ...(group.rules ?? [])]))
    .filter(text => !cardLines.has(normalizeText(text!)));
  const duplicateCardLines = new Set(modifierDescriptions
    .filter(value => value.text && value.displayText && normalizeText(value.text) !== normalizeText(value.displayText)
      && !cardLines.has(normalizeText(value.displayText)))
    .map(value => normalizeText(value.text!)));
  const displayedCardDescription = cardDescription.split(/\r?\n/)
    .filter(line => !duplicateCardLines.has(normalizeText(line)))
    .join('\n');
  return <>
    {group.summary && <p>{group.summary}</p>}
    {card && <>
      <SimpleMarkdown text={displayedCardDescription} keyPrefix={group.key} />
      {card.type === 'Rumor' && <div className="game-rumor-progress">
        {rumorProgress(card).map(value => <span key={value}>{value}</span>)}
        {(card.rumorLocation || card.activityLocationName) && <p><strong>Location:</strong> {card.rumorLocation || card.activityLocationName}</p>}
        {card.rumorPassCondition && <p><strong>Pass:</strong> {card.rumorPassCondition}</p>}
        {detailed && card.rumorPassReward && <p><strong>Reward:</strong> {card.rumorPassReward}</p>}
        {card.rumorFailCondition && <p><strong>Fail:</strong> {card.rumorFailCondition}</p>}
        {detailed && card.rumorFailPenalty && <p><strong>Penalty:</strong> {card.rumorFailPenalty}</p>}
      </div>}
      {card.shouldBeActiveForOneTurn && <span className="game-rule-meta">Until end of turn</span>}
    </>}
    {(!card || detailed) && <div className="game-rule-effects">{descriptions.map(text => <div key={text}>{text}
      {group.modifiers.filter(modifier => text?.startsWith(modifier.textDescription?.trim() || modifier.additionalData?.trim() || '\u0000')).some(modifier => modifier.used) && <span className="game-item-state">Used</span>}
    </div>)}</div>}
    {!card && !group.item && !group.summary && descriptions.length === 0 && <p>See the original card for its rules.</p>}
  </>;
};

export const GameRules: React.FC<{ state: GameState; hideHeading?: boolean; includeAct?: boolean; actions?: UserAction[] }> = ({ state, hideHeading = false, includeAct = false, actions = [] }) => {
  const [ownerToShow, setOwnerToShow] = useState<Investigator | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const groups = buildRuleGroups(state);
  if (includeAct && state.kingInYellowAct) groups.push({ key: 'rule-current-act', name: `King in Yellow: Act ${state.kingInYellowAct}`, type: 'Act', modifiers: [], artwork: `/acts/act${state.kingInYellowAct}.jpg` });
  const group = groups.find(value => value.key === selected);
  const isAncientOne = group?.type === 'AncientOne' && group.name === state.ancientOne?.name;
  const investigatorSource = group?.type === 'Investigator' ? group.modifiers
    .map(modifier => modifier.sourceCard as Investigator | undefined)
    .find(card => card?.name?.toLowerCase() === group.name.toLowerCase() && typeof card.abilityDescription === 'string') : undefined;
  const investigator = group?.type === 'Investigator' ? orderedGameInvestigators(state, investigatorSource ? [investigatorSource] : [])
    .find(value => value.name.toLowerCase() === group.name.toLowerCase()) : undefined;
  if (!groups.length) return null;
  return <section className="game-rules" id="game-rules" tabIndex={-1} aria-label="Rules in play">{!hideHeading && <h3>Rules in play</h3>}
    <div className="game-rules-grid">{groups.map(value => <article key={value.key} id={value.key} className="game-rule-group">
      <div className="game-rule-heading"><strong>{value.name}</strong><a href="#game-rules" className="game-rule-type" onClick={event => {
        event.preventDefault();
        setSelected(value.key);
      }} aria-label={`View ${ruleTypeLabel(value)} details for ${value.name}`} aria-haspopup="dialog">{ruleTypeLabel(value)}</a></div>
      <RuleText group={value} detailed />
    </article>)}</div>
    {group && (investigator
      ? <InvestigatorInspector key={group.key} state={state} investigator={investigator} actions={actions} onClose={() => setSelected(null)} />
      : isAncientOne
      ? <AncientOneDetails state={state} onClose={() => setSelected(null)} />
      : <GameDialog title={group.name} onClose={() => setSelected(null)}>
          {group.type === 'Act' ? <img className="game-act-artwork" src={group.artwork} alt={group.name} /> : group.type === 'Herald' ? <div className="game-rule-artwork"><HeraldCard herald={{ name: group.name }} /></div>
            : group.type === 'Guardian' ? <div className="game-rule-artwork"><GuardianCard guardian={{ name: group.name }} /></div>
            : group.type === 'Institution' && state.activeInstitution ? <InstitutionDisplay institution={state.activeInstitution} locations={state.locations} />
            : group.item ? <><Card card={group.item} />
                {group.item.item.name === 'Ritual Candles' && group.item.item.usesRemaining != null && <p>Clues on card: {group.item.item.usesRemaining}</p>}
                {group.owner && <p>Owner: <button type="button" onClick={() => { setOwnerToShow(group.owner!); setSelected(null); }}>{group.owner.name}</button></p>}
                <RuleText group={group} detailed /></>
            : <RuleText group={group} detailed />}
        </GameDialog>)}
    {ownerToShow && <InvestigatorInspector state={state} investigator={ownerToShow} actions={actions} onClose={() => setOwnerToShow(null)} />}
  </section>;
};

export const AncientOneDetails: React.FC<{ state: GameState; onClose: () => void }> = ({ state, onClose }) => {
  const ancient = state.ancientOne;
  return <GameDialog title={ancient?.name || 'Ancient One'} onClose={onClose}>
    <AncientOneDisplay ancientOne={ancient} isFinalBattle={state.isFinalBattle} ancientOneDamageDealt={state.ancientOneDamageDealt} ancientOneDamageThreshold={state.ancientOneDamageThreshold} responsive />
  </GameDialog>;
};
